import { pack, IFaceted } from '@ms/items-view/lib/private/models/Facet';
import { ItemResolver } from '@ms/items-view/lib/private/models/items/ItemResolver';

import {
  childItemsFacet,
  defaultChildItemSetFacet,
  filterOptionSetsFacet,
  parentItemFacet,
  sortFacet
} from '@ms/items-view/lib/private/facets/SetFacets';
import { allGroupsCollapsedFacet } from '@ms/items-view/lib/private/facets/ScopeFacets';
import { getDriveItemKey, getEndpointVersionKeyAliases } from '@ms/items-view-addon-graph/lib/utilities/getDriveItemKey';
import { driveItemKeyFacet } from '@ms/items-view-addon-graph/lib/models/keys/DriveItemKeyFacet';
import { driveAnalyticsItemKeyFacet } from '@ms/items-view-addon-graph/lib/models/keys/DriveAnalyticsItemKeyFacet';
import { driveFollowedItemKeyFacet } from '@ms/items-view-addon-graph/lib/models/keys/DriveFollowedItemKeyFacet';
import { driveActivityItemKeyFacet } from '@ms/items-view-addon-graph/lib/models/keys/DriveActivityItemKeyFacet';
import ColumnStrings from '@ms/items-view/lib/private/components/columns/columns.resx';
import {
  displayNameFacet,
  folderFacet,
  parentFolderItemFacet,
  deletedFacet,
  openUrlFacet,
  modifiedByPersonFacet,
  fileSizeFacet,
  dateModifiedFacet,
  isSharedFacet,
  analyticsItemFacet,
  followedItemFacet,
  averageRatingFacet,
  ratingCountFacet,
  hasUserRatedFacet,
  userRatingFacet,
  allRatingsFacet,
  likesCountFacet,
  hasUserLikedFacet,
  fileNameFacet,
  isNewFacet,
  isCheckedOutFacet,
  malwareDetectedFacet,
  policyTipFacet,
  retentionPolicyFacet,
  showRetentionPolicyIconFacet,
  vroomUrlFacet,
  missingMetadataFacet,
  canonicalUrlFacet,
  likedByItemFacet,
  activityItemFacet,
  eTagFacet
} from '@ms/items-view/lib/private/facets/ItemFacets';

import {
  PolicyTipType,
  RetentionPolicyType
} from '@ms/items-view/lib/private/models/ItemPolicy';

import {
  preferredKeyFacetFacet
} from '@ms/items-view/lib/private/facets/OperationFacets';

import {
  isSortableFacet,
  isGroupableFacet,
  isFilterableFacet,
  isEditableFacet,
  isRequiredFacet
} from '@ms/items-view/lib/private/facets/ColumnFacets';

import {
  isGroupFacet,
  countFacet,
  defaultCollapsedFacet,
  isDropEnabledFacet
} from '@ms/items-view/lib/private/facets/GroupFacets';

import {
  oneNoteFacet,
  fileIconTypeFacet
} from '@ms/items-view/lib/private/facets/DocumentFacets';

import {
  thumbnailFacet, photoFacet, videoFacet, dimensionsFacet
} from '@ms/items-view/lib/private/facets/MediaFacets';

import {
  emailFacet,
  pictureFacet,
  sipFacet,
} from '@ms/items-view/lib/private/facets/PersonFacets';

import { getInvalidationFacet } from '@ms/items-view/lib/private/operations/invalidateItems';
import { FileIconType } from '@uifabric/file-type-icons/lib/index';
import IDouble from '@ms/odsp-datasources/lib/interfaces/IDouble';
import ISPGetItemResponse, {
  ISPListData, ISPListField, ISPListRow, IEditor, ISPContentType, ISPListSchema, IMediaServiceMetadata
} from '@ms/odsp-datasources/lib/dataSources/item/spListItemRetriever/interfaces/ISPGetItemResponse';
import {
  spFolderItemSetKeyFacet,
  spItemKeyFacet,
  spViewKeyFacet,
  spFieldKeyFacet,
  spItemGroupKeyFacet,
  spItemPageKeyFacet,
  spItemNextGroupPageKeyFacet,
  spItemTokenGroupPageKeyFacet,
  spPersonKeyFacet,
  spFilterOptionSetKeyFacet,
  spWebKeyFacet,
  spPageContextKeyFacet,
  spListContentTypeKeyFacet,
  spFolderContentTypeKeyFacet,
  spPinnedItemSetKeyFacet,
  needsAttentionViewId,
  spColumnAdapterKeyFacet,
  spItemFullUrlKeyFacet,
  likedByItemKeyFacet,
  spGridInitInfoKeyFacet,
  spListKeyFacet,
  spFolderItemSetPagingTokenKeyFacet
} from './Keys';
import {
  FieldType,
} from './interfaces/Fields';
import { IGroupSchemaMap, IGroupSchema } from '@ms/odsp-datasources/lib/dataSources/item/spListItemRetriever/interfaces/ISPListContext';
import {
  internalNameFacet,
  fieldTypeFacet,
  fieldFieldTypeFacet,
  mappedColumnFieldTypeFacet,
  realFieldNameFacet,
  customFormatterFacet,
  defaultGroupedFacet,
  rowDataFacet,
  uniqueIdFacet,
  listItemIdFacet,
  permissionsFacet,
  folderPathFacet,
  documentSetFacet,
  getFieldFacet,
  titleFacet,
  groupValueFacet,
  groupFilterByValueFacet,
  groupStringFacet,
  parentGroupKeyFacet,
  defaultDocumentLibraryUrlFacet,
  spItemIdentifierFacet,
  spListContentTypeFacet,
  spFolderContentTypeFacet,
  preferredViewFacet,
  spGroupFieldFacet,
  officeAppFacet,
  isReadOnlyFacet,
  serverViewFacet,
  currentDerivedViewFacet,
  missingMetadataFieldsFacet,
  showViewModifiedIndicatorFacet,
  contentTypeIdFacet,
  fieldIdFacet,
  isCalculatedFacet,
  columnTotalsFacet,
  friendlyDateModifiedFacet,
  launchBehaviorFacet,
  linkLocationFacet,
  fieldDataFacet,
  userLookupFieldFacet,
  spAggregatesFacet,
  isCountRelatedFacet,
  moderationStatusTypeFacet,
  moderationCommentsFacet,
  gridInitInfoFacet,
  fieldDispFormUrlFacet,
  prevHrefFacet,
  nextHrefFacet
} from './Facets';
import ISpPageContext from '@ms/odsp-datasources/lib/interfaces/ISpPageContext';
import { ISPListGroup, IAggregateData } from '@ms/odsp-datasources/lib/dataSources/item/spListItemProcessor/ISPListItemData';
import { GroupBuilder } from '@ms/odsp-datasources/lib/dataSources/item/spListItemProcessor/GroupBuilder';
import { fromHexString } from '@ms/odsp-datasources/lib/dataSources/item/spListItemProcessor/ExternalHelpers';
import { normalizeLower } from '@ms/items-view-utilities/lib/guid/Guid';
import ListFilterUtilities from '@ms/odsp-datasources/lib/utilities/list/ListFilterUtilities';
import { getFilterXmlToFetchNextGroup } from '@ms/odsp-datasources/lib/dataSources/item/spListItemRetriever/ListItemPostDataHelpers';
import { SchemaBuilder, EXCLUDED_FIELDS } from '@ms/odsp-datasources/lib/dataSources/item/spListItemProcessor/SchemaBuilder';
import {
  isGenericList,
  ListTemplateType,
  isPromotedLinksList as checkIsPromotedLinksList
} from '@ms/odsp-datasources/lib/dataSources/listCollection/ListTemplateType';
import { getRelativeDateTimeStringForLists } from '@ms/odsp-utilities/lib/dateTime/DateTime';
import { ItemUrlHelperBasic } from '@ms/odsp-datasources/lib/utilities/url/ItemUrlHelperBasic';
import * as Path from '@ms/odsp-datasources/lib/utilities/path/Path';
import * as StringHelper from '@ms/utilities-strings/lib/index';
import strings from '../dataSource/SPOFiles.resx';
import { isCheckedOut, isCheckedOutByUser, isCheckedOutByOther } from '../utilities/item/checkedOutHelper';
import { getComplianceRecordIconValue, getComplianceRecordValue } from '../utilities/item/complianceHelper';
import { getListItemFormUrl } from '@ms/odsp-datasources/lib/utilities/url/ListItemFormUrlHelper';
import { buildItemMediaTransformFacets } from '../utilities/mediaTransform/MediaTransformFacetBuilder';
import { IFeature } from '@ms/odsp-utilities/lib/features/FeaturesBasic';
import Guid from '@ms/odsp-utilities/lib/guid/Guid';
import View from '@ms/odsp-datasources/lib/models/view/View';
import { itemMediaTransformFacet } from '../models/facets/MediaTransformFacets';
import { setColumnWidthsFromViewToLocalStorage } from '../utilities/columnWidth/ColumnWidthHelper';
import { IListViewAdapter, IColumn } from '@ms/odsp-datasources/lib/interfaces/clientSideExtensions/ISpfxAdapter';
import {
  clientSideComponentIdFacet,
  clientSideComponentPropertiesFacet,
  columnAdapterKeyFacet,
  columnAdapterFacet,
  rowAdapterFacet,
  listViewAdapterFacet
}
  from '../models/facets/ClientSideExtensionFacets';
import { SimpleUri } from '@ms/odsp-utilities/lib/uri/SimpleUri';
import { resolveNewPage } from '@ms/items-view/lib/private/models/items/PageHelpers';
import { showInFiltersPaneStatusFacet, choicesFacet, isLinksListUrlFieldFacet } from '../models/facets/ColumnFacets';
import { ShowInFiltersPaneStatus } from '@ms/odsp-datasources/lib/dataSources/item/spListItemProcessor/SPListItemEnums';
import { IItemCacheThunkAction, IItemCacheDispatch } from '@ms/items-view/lib/private/store/actions/ItemCacheActions';
import { MirrorPatch } from '@ms/items-view/lib/private/utilities/Graft';
import {
  enableSharePointBackingItemsFacet,
  configurationItemKey
} from '@ms/items-view/lib/private/facets/ConfigurationFacets';
import { enableDebugClientSideExtensionsFacet } from '../dataSource/Facets';
import { readItem } from '@ms/items-view/lib/private/operations/readItem';
import { IServerView } from '@ms/odsp-datasources/lib/interfaces/view/IServerView';
import { getFilteredItemSetKey } from '../models/keys/getFilteredItemSetKey';
import { getNormalizedFolderKey } from '../models/keys/getNormalizedFolderKey';
import { Key } from '@ms/items-view/lib/private/models/KeyFacet';
import { readItems } from '@ms/items-view/lib/private/operations/readItems';
import { getFeatureChecker } from '../models/features/getFeatureChecker';
import { getAggregateDisplayValue } from '../models/columns/ColumnAggregateType';
import { getKillSwitch } from '@ms/odsp-utilities/lib/killswitch/Killswitch';
import { getParentItemKey } from './operations/getAllAncestorItems';
import { ModerationStatusType } from './interfaces/ModerationStatusType';
import { ICustomizedFieldsDict } from '../utilities/list/ListExtensionHelper';

interface IGroup {
  group: ISPListGroup;
  groupKey: typeof spItemGroupKeyFacet.type;
  parentGroupKey?: string;
  groupFieldName: string;
  groupFieldType: FieldType;
  childItemKeys: string[];
  childGroupKeys: string[];
  nextPageKey?: string;
  groupFilterByValue?: string;
}

interface IFieldCustomizerData {
  [fieldKey: string]: {
    clientSideComponentId?: string;
    clientSideComponentProperties?: string;
  };
}

const DocumentSetsModernView = { ODB: 1114, Fallback: false };
const IPRecordVersioningClient: IFeature = { ODB: 1261 };

const userFacets = {
  key: spPersonKeyFacet,
  displayName: displayNameFacet,
  email: emailFacet,
  picture: pictureFacet,
  sip: sipFacet
};

const ShowColumnTotals: IFeature = { ODB: 1192 };

export interface IProcessedResponse {
  itemResolver: ItemResolver;
  columnKeys: string[];
  childKeys: string[];
  filterOptionSets: typeof filterOptionSetsFacet.type;
  parentFolderPermissons: IDouble | undefined;
  progId: string | undefined;
  groupChildKeys: { [key: string]: string[] | undefined };
  groupLevel: number | undefined;
  hasTitle: string | undefined;
  pageContext: ISpPageContext | undefined;
  itemSetGetAsItemKey: typeof spFolderItemSetKeyFacet.type | undefined;
  contentTypeKeys: string[];
  folderContentTypeKeys: string[];
  pinnedItemKeys: string[] | undefined;
  pinnedItemSetKey: typeof spPinnedItemSetKeyFacet.type;
  defaultViewKey: string | undefined;
  canModifySchema: boolean;
  hasFieldCustomizer: boolean | undefined;
  spFolderAggregateData: { [index: string]: IAggregateData; } | undefined;
  isRootFolder: boolean | undefined;
  isDocSet?: boolean;
}

export interface IProcessResponseOptions {
  /**
   * The original list response, in its entirety.
   */
  serverData: ISPGetItemResponse;
  /**
   * The expanded key for the current folder set which was requested.
   */
  itemSetKey: typeof spFolderItemSetKeyFacet.type;
  /**
   * The current known page context backing the list, if available.
   * (The response may contain a new page context, in which case that should be used instead.)
   */
  currentPageContext: ISpPageContext | undefined;
  /**
   * Known content types for the request, if available.
   * (The response may contain new content types, which should be used instead.)
   */
  contentTypes: ISPContentType[] | undefined;
  /**
   * The key for the current parent group being filled, if applicable.
   */
  parentGroupKey?: string;
  /**
   * Whether or not the next group's XML query should have been part of the response.
   */
  fetchNextGroupXml?: string;
  /**
   * The configured client-side extension, if applicable.
   */
  listViewAdapter?: IListViewAdapter;
  /**
   * The current page being filled, if applicable.
   */
  pageItemKey?: string;
  /**
   * The next grouping level for the group in the response, if applicable.
   */
  nextGroupLevel?: number;
  /**
   * Whether or not the response reflects the content of the provided item set.
   */
  isItemSetRequest: boolean;
}

/**
 * Processes a response from the SharePoint RenderListDatraAsStream or GetListDataAsStream API.
 * @internal
 */
export function processResponse(options: IProcessResponseOptions): IItemCacheThunkAction<IProcessedResponse> {
  return (dispatch: IItemCacheDispatch) => {
    const {
      serverData,
      itemSetKey,
      currentPageContext,
      contentTypes: existingListContentTypes,
      parentGroupKey,
      fetchNextGroupXml,
      listViewAdapter,
      pageItemKey,
      nextGroupLevel,
      isItemSetRequest
    } = options;

    const {
      webAbsoluteUrl,
      listFullUrl,
      rootFolder
    } = itemSetKey;

    const parentItemKey: typeof spFolderItemSetKeyFacet.type = {
      webAbsoluteUrl,
      listFullUrl,
      rootFolder
    };
    const isODB = Number(serverData.ListTemplateType) === ListTemplateType.mySiteDocumentLibrary;
    const isLinksList = Number(serverData.ListTemplateType) === ListTemplateType.links;
    const groupByFieldKey = itemSetKey.groupByFieldKey;
    const serializedParentItemKey = spItemKeyFacet.serialize(parentItemKey);
    const serializedItemSetKey = spFolderItemSetKeyFacet.serialize(itemSetKey);

    const listData: ISPListData = serverData.ListData || serverData as ISPListData;
    const pageContextInfo = serverData.PageContextInfo;
    const spotlightInfo = serverData.SpotlightInfo;
    const pageContext: ISpPageContext | undefined = pageContextInfo ? JSON.parse(pageContextInfo) : currentPageContext;
    const expFeatures = (pageContext && pageContext.ExpFeatures) || [];

    const isExpFeatureEnabled = dispatch(readItems(getFeatureChecker));
    let myListViewAdapter = listViewAdapter;
    if (!myListViewAdapter) {
      const listKey = spListKeyFacet.serialize({
        webAbsoluteUrl: parentItemKey.webAbsoluteUrl,
        listFullUrl: parentItemKey.listFullUrl
      });

      const {
        listAdapter
      } = dispatch(readItem({
        listAdapter: listViewAdapterFacet
      }, listKey));
      myListViewAdapter = listAdapter;
    }

    const isFeatureEnabled = (feature: IFeature) => isExpFeatureEnabled(feature, expFeatures);

    const isDocSet = useDocSetsModernView(isFeatureEnabled) &&
      listData && listData.CurrentFolderProgId === 'Sharepoint.DocumentSet';

    // Currently ODB doesn't allow to modify schema.
    const canModifySchema = !isODB && !!(serverData.BasePermissions && (
      serverData.BasePermissions.ManageLists || serverData.BasePermissions.ManagePersonalViews) && (
        !itemSetKey.viewKey || itemSetKey.viewKey.viewId !== needsAttentionViewId
      ));

    const itemResolver = new ItemResolver();

    const columnKeys: string[] = [];
    const childKeys: string[] = [];
    const contentTypeKeys: string[] = [];
    const folderContentTypeKeys: string[] = [];
    let pinnedItemKeys: string[] | undefined = undefined;
    let pinnedItemSet: typeof spPinnedItemSetKeyFacet.type;
    const filterOptionSets: typeof filterOptionSetsFacet.type = {};
    let itemSetGetAsItemKey: typeof spFolderItemSetKeyFacet.type | undefined = undefined;
    let defaultViewKey: string | undefined = undefined;
    let defaultDocumentLibraryUrl: string | undefined = undefined;
    let preferredItemSetKey = itemSetKey;
    let viewKey = preferredItemSetKey.viewKey;
    let hasFieldCustomizer = false;

    // set aggregate data
    let spFolderAggregateData: { [index: string]: IAggregateData; } | undefined;
    let mapFieldNameToColumnKey: { [index: string]: string; } = {};
    let aggregatesMetadata: { [index: string]: string; } = {};

    let isAggregatesOn: boolean = serverData.ListSchema !== undefined &&
      serverData.ListSchema.Aggregate !== undefined && serverData.ListData !== undefined &&
      serverData.ListData.FirstRow !== undefined && serverData.ListData.Row !== undefined &&
      serverData.ListData.Row.length > 0;

    // compute aggregatesMetadata, this will help to create mapFieldNameToColumnKey
    if (isAggregatesOn) {
      aggregatesMetadata = (serverData.ListSchema as ISPListSchema).Aggregate as { [index: string]: string; };
    }

    // When request for itemSet of default view, the original itemSetKey might not contain view key.
    // We need to use the server returned view Id to update view key in the preferredItemSetKey.
    // And map the original itemSetKey to the new prefered keys.
    if (!viewKey) {
      let serverReturnedViewGuid = (serverData.ListSchema && serverData.ListSchema.View) || serverData.view ||
        (serverData.ViewMetadata && serverData.ViewMetadata.Id);
      if (serverReturnedViewGuid) {
        viewKey = {
          viewId: Guid.normalizeLower(serverReturnedViewGuid, false/*strip brackets*/)
        };
      }

      // Set the real default view key in preferredItemSetKey.
      preferredItemSetKey = {
        ...preferredItemSetKey,
        viewKey: viewKey
      };
      itemSetGetAsItemKey = preferredItemSetKey;

      if (isItemSetRequest) {
        itemResolver.mapKeys({
          mappings: {
            [spFolderItemSetKeyFacet.serialize(itemSetGetAsItemKey)]: serializedItemSetKey
          }
        });
      }
    }

    if (viewKey) {
      const serializedViewKey = spViewKeyFacet.serialize(viewKey);

      if (serverData.ViewMetadata && serverData.ViewMetadata.DefaultView) {
        defaultViewKey = serializedViewKey;
      }

      // TODO: factor out this logic to be reused in odsp-datasource ListItemBuilderHelpers.ts
      // RenderListDataAsStream doesn't return all ServerView properties in viewMetadata. We need
      // to ensure get all these information into the ServerView object.
      let serverView: IServerView | undefined;
      if (serverData.ViewMetadata) {
        serverView = { ...serverData.ViewMetadata };
        if (serverData.VisualizationInfo) {
          try {
            serverView.VisualizationInfo = JSON.parse(serverData.VisualizationInfo);
          } catch (ex) {
            // Most likely not a VisualizationInfo object. Skip this property...
          }
        }
        serverView.CustomFormatter = serverData.ListSchema && serverData.ListSchema.CustomFormatter;
      }

      const currentDerivedView = serverView ? new View(serverView) : undefined;
      if (currentDerivedView) {
        setColumnWidthsFromViewToLocalStorage(currentDerivedView);
      }

      if (isItemSetRequest) {
        const gridInitInfoKey = spGridInitInfoKeyFacet.serialize({
          listKey: {
            webAbsoluteUrl: itemSetKey.webAbsoluteUrl,
            listFullUrl: itemSetKey.listFullUrl
          },
          viewKey: itemSetKey.viewKey
        });

        itemResolver.resolveItems({
          items: {
            [serializedViewKey]: {
              ...getInvalidationFacet()
                .forFacet(serverViewFacet).thenInvalidate(serializedItemSetKey, childItemsFacet)
                .forFacet(currentDerivedViewFacet).thenInvalidate(serializedItemSetKey, childItemsFacet)
                .forFacet(spAggregatesFacet).thenInvalidate(serializedItemSetKey, childItemsFacet)
                .forFacet(serverViewFacet).thenInvalidate(gridInitInfoKey, gridInitInfoFacet)
                .forFacet(currentDerivedViewFacet).thenInvalidate(gridInitInfoKey, gridInitInfoFacet)
                .pack(),
              ...(serverView ? {
                ...serverViewFacet.pack(serverView),
                ...currentDerivedViewFacet.pack(currentDerivedView),
                ...showViewModifiedIndicatorFacet.pack(false)
              } : {})
            }
          }
        });
      }
    }

    // When request for itemSet of default library in a web, the original itemSetKey might not contain listFullUrl.
    // We need to use server returned list Url to update itemSetKey.
    // And map the original itemSetKey to the new prefered keys.
    if (parentItemKey.listFullUrl === '' && serverData.listUrlDir) {
      const itemUrlHelperWithListUrlDir = new ItemUrlHelperBasic({
        context: {
          webAbsoluteUrl: parentItemKey.webAbsoluteUrl,
          listUrl: decodeURIComponent(serverData.listUrlDir)
        }
      });

      defaultDocumentLibraryUrl = itemUrlHelperWithListUrlDir.getUrlParts({}).fullListUrl;

      if (defaultDocumentLibraryUrl) {
        const preferredParentItem: typeof spItemKeyFacet.type = {
          webAbsoluteUrl,
          rootFolder,
          listFullUrl: defaultDocumentLibraryUrl
        };

        preferredItemSetKey = {
          ...preferredItemSetKey,
          listFullUrl: defaultDocumentLibraryUrl
        };

        itemSetGetAsItemKey = preferredItemSetKey;

        if (isItemSetRequest) {
          const preferredParentItemKey = spItemKeyFacet.serialize(preferredParentItem);

          itemResolver.mapKeys({
            mappings: {
              // Mapping the original itemSetKey, parentItemKey to the new prefered keys.
              // This will make sure itemCache can return the same item using
              // original key or preferred key.
              [spFolderItemSetKeyFacet.serialize(itemSetGetAsItemKey)]: serializedItemSetKey,
              [preferredParentItemKey]: serializedParentItemKey
            }
          });

          if (!pageContext) {
            // We will only hit this code when no pageContext in the response and
            // it is for default document library.
            // This means we already have pageContext using the empty listFullUrl,
            // we need to map the existing pageContext with the new key.
            // This will ensure itemCache will return the existing pageContext
            // when use the actual defaultDocumentLibraryUrl
            itemResolver.mapKeys({
              mappings: {
                [spPageContextKeyFacet.serialize({
                  webAbsoluteUrl: preferredParentItem.webAbsoluteUrl,
                  listFullUrl: preferredParentItem.listFullUrl
                })]: spPageContextKeyFacet.serialize({
                  webAbsoluteUrl: parentItemKey.webAbsoluteUrl,
                  listFullUrl: parentItemKey.listFullUrl
                })
              }
            });
          }
        }

        itemResolver.resolveItems({
          items: {
            [spWebKeyFacet.serialize({
              webAbsoluteUrl: parentItemKey.webAbsoluteUrl
            })]: {
              ...defaultDocumentLibraryUrlFacet.pack(defaultDocumentLibraryUrl)
            },
            ...(isItemSetRequest ? {
              [serializedParentItemKey]: {
                ...spItemKeyFacet.pack(preferredParentItem),
                ...preferredKeyFacetFacet.pack(spItemKeyFacet)
              }
            } : {})
          }
        });
      }
    }

    if (!webAbsoluteUrl && serverData.HttpRoot) {
      const realWebAbsoluteUrl = decodeURIComponent(serverData.HttpRoot);

      const preferredParentItem: typeof spItemKeyFacet.type = {
        webAbsoluteUrl: realWebAbsoluteUrl,
        rootFolder,
        listFullUrl
      };

      preferredItemSetKey = {
        ...preferredItemSetKey,
        webAbsoluteUrl: realWebAbsoluteUrl
      };

      itemSetGetAsItemKey = preferredItemSetKey;

      if (isItemSetRequest) {
        const preferredParentItemKey = spItemKeyFacet.serialize(preferredParentItem);

        itemResolver.mapKeys({
          mappings: {
            // Mapping the original itemSetKey, parentItemKey to the new prefered keys.
            // This will make sure itemCache can return the same item using
            // original key or preferred key.
            [spFolderItemSetKeyFacet.serialize(itemSetGetAsItemKey)]: serializedItemSetKey,
            [preferredParentItemKey]: serializedParentItemKey
          }
        });

        if (!pageContext) {
          // We will only hit this code when no pageContext in the response and
          // it is for the returned web.
          // This means we already have pageContext using the empty webAbsoluteUrl,
          // we need to map the existing pageContext with the new key.
          // This will ensure itemCache will return the existing pageContext
          // when use the actual web URL.
          itemResolver.mapKeys({
            mappings: {
              [spPageContextKeyFacet.serialize({
                webAbsoluteUrl: preferredParentItem.webAbsoluteUrl,
                listFullUrl: preferredParentItem.listFullUrl
              })]: spPageContextKeyFacet.serialize({
                webAbsoluteUrl: parentItemKey.webAbsoluteUrl,
                listFullUrl: parentItemKey.listFullUrl
              })
            }
          });
        }

        itemResolver.resolveItems({
          items: {
            [serializedParentItemKey]: {
              ...spItemKeyFacet.pack(preferredParentItem),
              ...preferredKeyFacetFacet.pack(spItemKeyFacet)
            }
          }
        });
      }
    }

    const itemUrlHelper = new ItemUrlHelperBasic({
      context: {
        webAbsoluteUrl: parentItemKey.webAbsoluteUrl,
        listUrl: preferredItemSetKey.listFullUrl
      }
    });
    let isRootFolder = !rootFolder;
    if (rootFolder) {
      const itemUrlParts = itemUrlHelper.getUrlParts({
        path: rootFolder,
        listUrl: parentItemKey.listFullUrl,
        webUrl: parentItemKey.webAbsoluteUrl
      });
      isRootFolder = itemUrlParts.serverRelativeItemUrl === itemUrlParts.serverRelativeListUrl;
    }

    const fieldInfoMap: {
      [key: string]: FieldType
    } = {};
    let sharedWidthColumnIndex = -1;

    let originalGroupBy: string[] = [];
    let groupBy: string[] = [];
    let areGroupsCollapsed: boolean | undefined = serverData.ListSchema && serverData.ListSchema.Collapse === 'TRUE';
    let haveResolvedItems = false;
    let groupSchema: IGroupSchemaMap = {};

    if (serverData.ListSchema && serverData.ListSchema.group1) {
      groupBy.push(serverData.ListSchema.group1);
      if (serverData.ListSchema.group2) {
        groupBy.push(serverData.ListSchema.group2);
      }
    }

    if (preferredItemSetKey.filterParams) {
      const filterList = ListFilterUtilities.getFilterList(preferredItemSetKey.filterParams);
      let filterByGroup0 = false;
      let filterByGroup1 = false;
      for (const filter of filterList) {
        if (filter.values.length === 1) {
          if (filter.fieldName === groupBy[0]) {
            filterByGroup0 = true;
          } else if (filter.fieldName === groupBy[1]) {
            filterByGroup1 = true;
          }
        }
      }
      originalGroupBy = groupBy;
      if (filterByGroup0 && filterByGroup1) {
        groupBy = [];
      } else if (filterByGroup0) {
        groupBy = groupBy.slice(1, 2);
      } else if (filterByGroup1) {
        groupBy = groupBy.slice(0, 1);
      }
    }

    const newListContentTypes = serverData.AvailableContentTypes || serverData.ListContenTypes;
    if (newListContentTypes) {
      newListContentTypes.forEach((contentType: ISPContentType) => {
        const spListContentTypeKey: typeof spListContentTypeKeyFacet.type = {
          listKey: {
            webAbsoluteUrl: preferredItemSetKey.webAbsoluteUrl,
            listFullUrl: preferredItemSetKey.listFullUrl
          },
          contentTypeId: contentType.contentTypeId || ''
        };
        const contentTypeKey = spListContentTypeKeyFacet.serialize(spListContentTypeKey);

        itemResolver.resolveItems({
          items: {
            [contentTypeKey]: {
              ...spListContentTypeFacet.pack(contentType)
            }
          }
        });

        contentTypeKeys.push(contentTypeKey);
      });
    }

    const folderContentTypeOrder = serverData.FolderContentTypeOrder || newListContentTypes || existingListContentTypes;
    if (folderContentTypeOrder) {
      folderContentTypeOrder.forEach((contentType: ISPContentType) => {
        const spFolderContentTypeKey: typeof spFolderContentTypeKeyFacet.type = {
          folderKey: {
            webAbsoluteUrl: preferredItemSetKey.webAbsoluteUrl,
            listFullUrl: preferredItemSetKey.listFullUrl,
            rootFolder: preferredItemSetKey.rootFolder
          },
          contentTypeId: contentType.contentTypeId || ''
        };
        const contentTypeKey = spFolderContentTypeKeyFacet.serialize(spFolderContentTypeKey);

        itemResolver.resolveItems({
          items: {
            [contentTypeKey]: {
              ...spFolderContentTypeFacet.pack(contentType)
            }
          }
        });

        folderContentTypeKeys.push(contentTypeKey);
      });
    }

    const userFieldLookupFields: { [fieldName: string]: string } = {};
    if (serverData.ListSchema && serverData.ListSchema.Field) {
      if (isODB) {
        // if ODB schema doesn't have size field (which is the default), add that field.
        if (!serverData.ListSchema.Field.some((field: ISPListField) => {
          return field.Name === 'FileSizeDisplay';
        })) {
          const sizeField = {
            'Name': 'FileSizeDisplay',
            'FieldType': 'Computed',
            'RealFieldName': 'FileSizeDisplay',
            'DisplayName': ColumnStrings.FileSize,
            'ID': '78a07ba4-bda8-4357-9e0f-580d64487583',
            'StaticName': 'FileSizeDisplay',
            'ReadOnly': 'TRUE',
            'role': 'Computed',
            'ariaLabel': 'File Size',
            'FromBaseType': 'TRUE',
            'Type': 'Computed',
            'AllowGridEditing': 'FALSE',
            'ClientSideComponentId': '00000000-0000-0000-0000-000000000000',
            'PinnedToFiltersPane': 'FALSE',
            'ShowInFiltersPane': 'Auto'
          };
          serverData.ListSchema.Field.push(sizeField);
        }
      }
      if (itemSetKey.viewKey && itemSetKey.viewKey.viewId === needsAttentionViewId) {
        // Add missing info field to needs attention view schema
        const missingInfoField = {
          'Name': 'MissingInfoFilesThatNeedAttention',
          'FieldType': 'Text',
          'DisplayName': 'Missing info',
          'ReadOnly': 'TRUE',
          'FromBaseType': 'TRUE',
          'Type': 'Text',
          'AllowGridEditing': 'FALSE',
          'PinnedToFiltersPane': 'FALSE',
          'ShowInFiltersPane': 'Removed',
          'Groupable': 'FALSE',
          'Filterable': 'FALSE',
          'Sortable': 'FALSE'
        };
        serverData.ListSchema.Field.splice(-1, 0, missingInfoField);
      }

      // Check for debug client-side extensions
      const {
        enableDebugClientSideExtensions
      } = dispatch(readItem({
        enableDebugClientSideExtensions: enableDebugClientSideExtensionsFacet
      }, configurationItemKey));

      let debugFieldCustomizers: IFieldCustomizerData | undefined;
      if (enableDebugClientSideExtensions && enableDebugClientSideExtensions.fieldCustomizers) {
        debugFieldCustomizers = prepareClientSideCustomizedFields(enableDebugClientSideExtensions.fieldCustomizers);
      }

      serverData.ListSchema.Field.forEach((field: ISPListField) => {
        if (!pageContext || !getKillSwitch(pageContext)
          .isActivated('5B080E5C-D2F5-4709-B603-C8353E21C386', '03/13/2018', 'Exclude certain fields from any returned list schema')) {
          if (EXCLUDED_FIELDS.indexOf(field.Name) !== -1) {
            return; // skip field if it's one of the excluded fields
          }
        }

        const spFieldKey: typeof spFieldKeyFacet.type = {
          internalName: field.Name
        };
        const columnKey = spFieldKeyFacet.serialize(spFieldKey);
        if (field.Name !== undefined && aggregatesMetadata[field.Name] !== undefined) {
          mapFieldNameToColumnKey[field.Name] = columnKey;
        }
        // Grouped fields appear twice in the list schema. Only add them to the list schema once.
        if (field.GroupField === 'TRUE' && groupSchema) {
          if (itemSetKey.sortFieldKey === field.Name) {
            (field as IGroupSchema).Ascending = itemSetKey.sortAscending ? 'true' : 'false';
          }
          groupSchema[field.Name] = field as IGroupSchema;
        } else {
          // We need to get the correctly fieldType for the field, which will be used to
          // in the processCoreItemResult. No matter whether this is for result from expanding group or
          // regular list.
          let fieldType = getFieldType(field);
          fieldInfoMap[field.Name] = fieldType;
          if (!!field.RealFieldName && field.RealFieldName !== field.Name) {
            fieldInfoMap[field.RealFieldName] = fieldType;
          }

          if (!parentGroupKey) {
            const isFilterable = !(field.Filterable === 'FALSE' || field.FilterDisable === 'TRUE' ||
              field.FieldType === 'Note' || field.FieldType === 'URL' ||
              field.Name === 'FileSizeDisplay' || field.Name === '_IsRecord');

            const isGroupable = (field.Groupable !== 'FALSE' && field.Sortable !== 'FALSE'
              && isGroupableColumn(field.Name, field.FieldType));

            if (isODB && field.Name === 'SharedWith') {
              sharedWidthColumnIndex = columnKeys.length;
            }

            // Hide user created columns from the needs attention view schema
            if (!(itemSetKey.viewKey && itemSetKey.viewKey.viewId === needsAttentionViewId) || field.FromBaseType === 'TRUE') {
              columnKeys.push(columnKey);
            }

            let columnAdapter = undefined;
            let columnAdapterKey = '';

            let clientSideComponentId = field.ClientSideComponentId || Guid.Empty;
            let clientSideComponentProperties = field.ClientSideComponentProperties || '';
            const fieldInterName = field.DisplayName || field.Name;
            if (debugFieldCustomizers && debugFieldCustomizers[fieldInterName]) {
              clientSideComponentId = debugFieldCustomizers[fieldInterName].clientSideComponentId || Guid.Empty;
              clientSideComponentProperties = debugFieldCustomizers[fieldInterName].clientSideComponentProperties || '';
            }

            if (clientSideComponentId && clientSideComponentId !== Guid.Empty) {
              columnAdapterKey = spColumnAdapterKeyFacet.serialize(spFieldKey);
            }

            if (listViewAdapter && field.ID) {
              let spfxColumn: IColumn = {
                id: field.ID,
                internalName: field.RealFieldName || field.Name,
                fieldType: field.FieldType || 'Text',
                isRequired: !!field.Required && field.Required.toUpperCase() === 'TRUE',
                isVisible: true, // Only fields included in current view are available
                displayName: fieldInterName,
                clientSideComponentId: clientSideComponentId,
                clientSideComponentProperties: clientSideComponentProperties,
                onSetVisible: (visible: boolean) =>
                  console.log('ISpfxAdapter.IColumn.onSetVisible not implemented'), // todo implement this
                onFieldCustomizerError: (error: Error) => {
                  console.log(`Fail to load customizer for column: ${field.Name},
                  componentId: ${field.ClientSideComponentId}, error: ${error.message}`);
                }
              };

              columnAdapter = listViewAdapter.addColumnAdapter(spfxColumn);
              if (!columnAdapter) {
                throw 'addColumnAdapter returns null';
              } else if (clientSideComponentId && clientSideComponentId !== Guid.Empty) {
                itemResolver.resolveItems({
                  items: {
                    [columnAdapterKey]: columnAdapterFacet.pack(columnAdapter)
                  }
                });
                hasFieldCustomizer = true;
              }
            }

            // Avoid storing higher high amounts of metadata which can increase memory consumption
            const {
              enableSharePointBackingItems = true
            } = dispatch(readItem({
              enableSharePointBackingItems: enableSharePointBackingItemsFacet
            }, configurationItemKey));

            itemResolver.resolveItems({
              items: {
                [columnKey]: {
                  ...spFieldKeyFacet.pack(spFieldKey),
                  ...internalNameFacet.pack(field.Name),
                  ...fieldTypeFacet.pack(fieldType),
                  ...fieldFieldTypeFacet.pack(field.FieldType),
                  ...fieldIdFacet.pack(field.ID),
                  ...mappedColumnFieldTypeFacet.pack(SchemaBuilder.getMappedColumnFieldType(field)),
                  ...displayNameFacet.pack(field.DisplayName),
                  ...realFieldNameFacet.pack(field.RealFieldName),
                  ...isSortableFacet.pack(field.Sortable !== 'FALSE'),
                  ...isGroupableFacet.pack(isGroupable),
                  ...isFilterableFacet.pack(isFilterable),
                  ...isEditableFacet.pack(field.FromBaseType !== 'TRUE'),
                  ...isReadOnlyFacet.pack(field.ReadOnly === 'TRUE'),
                  ...isCalculatedFacet.pack(field.role === 'Calculated'),
                  ...isCountRelatedFacet.pack(field.CountRelated === 'TRUE'),
                  ...customFormatterFacet.pack(field.CustomFormatter),
                  ...(!groupByFieldKey ? defaultGroupedFacet.pack(groupBy.indexOf(field.Name) !== -1) : {}),
                  ...fieldDispFormUrlFacet.pack(field.DispFormUrl),
                  ...isRequiredFacet.pack(!!field.Required && field.Required.toUpperCase() === 'TRUE'),
                  ...clientSideComponentIdFacet.pack(clientSideComponentId || Guid.Empty),
                  ...clientSideComponentPropertiesFacet.pack(clientSideComponentProperties || ''),
                  ...(columnAdapterKey ? columnAdapterKeyFacet.pack(columnAdapterKey) : {}),
                  ...(enableSharePointBackingItems ? { ...fieldDataFacet.pack(field) } : {}),
                  ...(field.ShowInFiltersPane !== undefined ? showInFiltersPaneStatusFacet.pack(
                    ShowInFiltersPaneStatus[field.ShowInFiltersPane as keyof typeof ShowInFiltersPaneStatus]
                  ) : {}),
                  ...(field.Choices ? choicesFacet.pack(field.Choices) : {}),
                  ...(field.LookupField ? userLookupFieldFacet.pack(field.LookupField) : {}),
                  ...(isLinksList && field.Name === 'URLwMenu' ? isLinksListUrlFieldFacet.pack(true) : {})
                }
              }
            });

            if (isFilterable) {
              filterOptionSets[columnKey] = spFilterOptionSetKeyFacet.serialize({
                itemSetKey: preferredItemSetKey,
                fieldKey: spFieldKey
              });
            }

            if (field.FieldType === 'User' && field.LookupField && field.RealFieldName) {
              userFieldLookupFields[field.RealFieldName] = field.LookupField;
            }
          }
        }
      });
    }

    if (isItemSetRequest && listData.FirstRow && (listData.NextHref || listData.PrevHref)) {
      // For itemset request, store previous page token and next page token.
      const tokenKey = {
        itemSetKey: preferredItemSetKey,
        currentFirstRow: listData.FirstRow
      };

      itemResolver.resolveItems({
        items: {
          [spFolderItemSetPagingTokenKeyFacet.serialize(tokenKey)]: {
            ...prevHrefFacet.pack(listData.PrevHref),
            ...nextHrefFacet.pack(listData.NextHref)
          }
        }
      });
    }

    // spFolder column aggregates
    if (isAggregatesOn) {
      const serverListData = (serverData.ListData as ISPListData);
      spFolderAggregateData = getAggregateData((serverListData.Row as ISPListRow[])[0] as ISPListRow,
        aggregatesMetadata, mapFieldNameToColumnKey, isFeatureEnabled);
    }

    let lastGroupKey0 = '';
    let lastGroupKey1 = '';
    let groupsToPack: { [key: string]: IGroup } = {};
    let groupChildKeys: { [key: string]: string[] | undefined } = {};
    const isCustomList = isGenericList(Number(serverData.ListTemplateType));
    const isPromotedLinks = checkIsPromotedLinksList(Number(serverData.ListTemplateType));
    const defaultFileIconType = isCustomList ? 4 as FileIconType.listItem : 3 as FileIconType.genericFile;
    if (listData && listData.Row) {
      if (parentItemKey.webAbsoluteUrl && parentItemKey.listFullUrl) {
        const itemUrlHelperWithListUrlDir = new ItemUrlHelperBasic({
          context: {
            webAbsoluteUrl: parentItemKey.webAbsoluteUrl,
            listUrl: parentItemKey.listFullUrl
          }
        });

        const urlParts = itemUrlHelperWithListUrlDir.getUrlParts({
          path: rootFolder
        });

        if (urlParts.fullItemUrl) {
          const fullUrlItemKey = spItemFullUrlKeyFacet.serialize({
            itemFullUrl: urlParts.fullItemUrl
          });

          itemResolver.mapKeys({
            mappings: {
              [fullUrlItemKey]: serializedParentItemKey
            }
          });
        }
      }

      if (listData.SortField && listData.SortDir) {
        itemResolver.resolveItems({
          items: {
            [serializedItemSetKey]: {
              ...sortFacet.pack({
                columnKey: spFieldKeyFacet.serialize({
                  internalName: listData.SortField
                }),
                isAscending: (listData.SortDir === 'ascending')
              })
            }
          }
        });
      }

      listData.Row.forEach((itemFromServer: ISPListRow) => {
        const coreItemResult = processCoreItemResult({
          currentUserId: serverData.CurrentUserId,
          itemFromServer: itemFromServer,
          serializedItemSetKey: serializedItemSetKey,
          serializedParentItemKey: serializedParentItemKey,
          preferredItemSetKey: preferredItemSetKey,
          itemUrlHelper: itemUrlHelper,
          defaultFileIconType: defaultFileIconType,
          isCustomList: isCustomList,
          fieldInfoMap: fieldInfoMap,
          groupBy: groupBy,
          childKeys: childKeys,
          displayFormUrl: serverData.displayFormUrl,
          contentTypes: newListContentTypes || existingListContentTypes,
          isFeatureEnabled: isFeatureEnabled,
          listViewAdapter: myListViewAdapter,
          isPromotedLinks: isPromotedLinks,
          isLinksList: isLinksList,
          userFieldLookupFields: userFieldLookupFields
        });

        itemResolver.merge(coreItemResult.itemResolver);

        if (coreItemResult.isItemResolved) {
          haveResolvedItems = true;
        }

        if (groupBy.length > 0) {
          const groupField0 = groupBy[0];
          const groupField1 = groupBy[1];
          if (groupField0) {
            if (itemFromServer[groupField0 + '.newgroup'] === '1') {
              let levelOverride = originalGroupBy.length > 0 ? originalGroupBy.indexOf(groupField0) : undefined;
              const newGroup = getGroup(groupSchema, itemFromServer, groupField0, 0, !!areGroupsCollapsed, preferredItemSetKey,
                viewKey, groupsToPack[lastGroupKey0], groupsToPack[lastGroupKey1], mapFieldNameToColumnKey, isFeatureEnabled,
                levelOverride, serverData.ListSchema);
              const newGroupKey = spItemGroupKeyFacet.serialize(newGroup.groupKey);
              childKeys.push(newGroupKey);
              lastGroupKey0 = newGroupKey;
              groupsToPack[newGroupKey] = newGroup;
            }
          }
          if (groupField1) {
            if (itemFromServer[groupField1 + '.newgroup'] === '1') {
              let levelOverride = originalGroupBy.length > 0 ? originalGroupBy.indexOf(groupField1) : undefined;
              const newGroup = getGroup(groupSchema, itemFromServer, groupField1, 1, !!areGroupsCollapsed, preferredItemSetKey,
                viewKey, groupsToPack[lastGroupKey0], groupsToPack[lastGroupKey1], mapFieldNameToColumnKey, isFeatureEnabled,
                levelOverride, serverData.ListSchema);
              const newGroupKey = spItemGroupKeyFacet.serialize(newGroup.groupKey);
              groupsToPack[lastGroupKey0].childGroupKeys.push(newGroupKey);
              newGroup.childItemKeys.push(coreItemResult.childKey);
              lastGroupKey1 = newGroupKey;
              groupsToPack[newGroupKey] = newGroup;
            } else {
              groupsToPack[lastGroupKey1].childItemKeys.push(coreItemResult.childKey);
            }
          } else {
            groupsToPack[lastGroupKey0].childItemKeys.push(coreItemResult.childKey);
          }
        }
      });
    }

    if (listData && listData.NextHref) {
      if (parentGroupKey || groupBy.length > 0) {
        // Get the remaining items in the last group to ensure there are 30 items
        const lastGroupKey = lastGroupKey1 ? lastGroupKey1 : lastGroupKey0;
        const lastGroup0 = groupsToPack[lastGroupKey0];
        const lastGroup1 = groupsToPack[lastGroupKey1];
        const parentKey = lastGroupKey || parentGroupKey;
        const groupedPageKey: typeof spItemTokenGroupPageKeyFacet.type = {
          itemSetKey: preferredItemSetKey,
          token: listData.NextHref.replace('DrillDown=1', 'IsGroupRender=TRUE'),
          parentGroupKey: parentKey || '' // TODO Fix the condition which leads to parentKey being undefined.
        };

        const spGroupedPageKey = spItemTokenGroupPageKeyFacet.serialize(groupedPageKey);

        itemResolver.merge(resolveNewPage({
          itemKey: spGroupedPageKey
        }).itemResolver);

        if (lastGroup1) {
          groupsToPack[lastGroupKey1].childItemKeys.push(spGroupedPageKey);
        } else if (lastGroup0) {
          groupsToPack[lastGroupKey0].childItemKeys.push(spGroupedPageKey);
        } else if (parentGroupKey) {
          childKeys.push(spGroupedPageKey);
        }

        // Get the next subgroup under the current group in a nested grouped view
        const lastGroupCount0 = lastGroup0 && lastGroup0.group.count;
        const lastGroupCount1 = lastGroup1 && lastGroup1.group.count;
        if (!parentGroupKey && lastGroup1 && lastGroupCount0 > lastGroupCount1) {
          const getItemSetKey = getFilteredItemSetKey(preferredItemSetKey);
          let groupFilterParams = undefined;
          if (lastGroup0.groupFilterByValue) {
            const groupFilteredSetKey = getItemSetKey({
              filterField: lastGroup0.groupFieldName,
              filterValues: [lastGroup0.groupFilterByValue],
              filterType: lastGroup0.groupFieldType,
              useLookupId: false
            });
            groupFilterParams = spFolderItemSetKeyFacet.deserialize(groupFilteredSetKey).filterParams;
          }
          let nextSubGroupXml = getFilterXmlToFetchNextGroup({
            ...lastGroup1.group,
            groupingId: lastGroup1.groupFilterByValue || lastGroup1.group.groupingId
          });
          const nextSubGroupPageKey: typeof spItemNextGroupPageKeyFacet.type = {
            itemSetKey: preferredItemSetKey,
            filterParams: groupFilterParams,
            nextGroupXml: nextSubGroupXml,
            nextGroupLevel: 1
          };
          const spNextSubGroupPageKey = spItemNextGroupPageKeyFacet.serialize(nextSubGroupPageKey);

          itemResolver.merge(resolveNewPage({
            itemKey: spNextSubGroupPageKey
          }).itemResolver);

          groupsToPack[lastGroupKey0].childGroupKeys.push(spNextSubGroupPageKey);
        }

        // Get the next first level group on the page
        if (!parentGroupKey && (lastGroup0 || fetchNextGroupXml)) {
          let nextGroupXml = lastGroup0 ? getFilterXmlToFetchNextGroup(lastGroup0.group) : fetchNextGroupXml;
          const nextGroupedPageKey: typeof spItemNextGroupPageKeyFacet.type = {
            itemSetKey: preferredItemSetKey,
            nextGroupXml: nextGroupXml || '', // TODO Fix the condition which leads to nextGroupXml being undefined.
            nextGroupLevel: 0,
            token: lastGroup0 ? undefined : listData.NextHref.replace('DrillDown=1', 'IsGroupRender=TRUE')
          };
          const spNextGroupedPageKey = spItemNextGroupPageKeyFacet.serialize(nextGroupedPageKey);

          itemResolver.merge(resolveNewPage({
            itemKey: spNextGroupedPageKey
          }).itemResolver);

          childKeys.push(spNextGroupedPageKey);
        }
      } else {
        const pageKey = spItemPageKeyFacet.serialize({
          itemSetKey: preferredItemSetKey,
          token: listData.NextHref
        });

        itemResolver.merge(resolveNewPage({
          itemKey: pageKey
        }).itemResolver);

        childKeys.push(pageKey);
      }
    }

    const groupLevel = groupBy.length > 0 ? groupBy.length - 1 : undefined;

    if (groupBy.length > 0 && isItemSetRequest) {
      itemResolver.resolveItems({
        items: {
          [serializedItemSetKey]: {
            ...allGroupsCollapsedFacet.pack(areGroupsCollapsed)
          }
        }
      });

      for (let groupKey in groupsToPack) {
        if (groupsToPack.hasOwnProperty(groupKey)) {
          let groupItem = groupsToPack[groupKey];
          let group = groupItem.group;
          let childItems = groupItem.childItemKeys.length > 0 && haveResolvedItems ? groupItem.childItemKeys :
            (groupItem.childGroupKeys.length > 0 ? groupItem.childGroupKeys : undefined);
          groupChildKeys[groupKey] = childItems;

          const isInvalidGroup =
            fetchNextGroupXml && (
              groupLevel === 1 && nextGroupLevel === 1 && groupKey === childKeys[0]
            ) ||
            (pageItemKey && (
              parentGroupKey === groupKey ||
              groupLevel === 1 && groupKey === childKeys[0]));

          if (isInvalidGroup) {
            continue;
          }

          itemResolver.resolveItems({
            items: {
              [groupKey]: {
                ...spItemGroupKeyFacet.pack(groupItem.groupKey),
                ...spGroupFieldFacet.pack({
                  internalName: groupItem.groupFieldName,
                  fieldType: groupItem.groupFieldType
                }),
                ...displayNameFacet.pack(group.name),
                ...pack({
                  totals: columnTotalsFacet
                }, {
                    totals: new MirrorPatch(group.columnTotals ? group.columnTotals : {})
                  }),
                ...groupValueFacet.pack(group.fieldValue),
                ...groupFilterByValueFacet.pack(groupItem.groupFilterByValue),
                ...groupStringFacet.pack(group.groupString),
                ...isGroupFacet.pack(true),
                ...isDropEnabledFacet.pack(group.isDropEnabled),
                ...countFacet.pack(group.count),
                ...defaultCollapsedFacet.pack(group.isCollapsed),
                ...(childItems ? childItemsFacet.pack({
                  itemKeys: childItems
                }) : {}),
                ...(groupItem.parentGroupKey ? {
                  ...parentGroupKeyFacet.pack({
                    itemKey: groupItem.parentGroupKey
                  })
                } : {})
              }
            }
          });
        }
      }
    }

    /**
     * viewKey in the original itemSetKey might be empty string.
     * But server always return the actual view Guid.
     * Edit pinned item operation require view Guid,
     * we need to ensure we use the view Guid returned from server
     * for pinnedItemSetKey.
     */
    pinnedItemSet = {
      parentItemKey: preferredItemSetKey,
      viewKey: viewKey
    };

    const pinnedItemSetKey = spPinnedItemSetKeyFacet.serialize(pinnedItemSet);

    if (spotlightInfo) {
      // We only initlaize pinnedItemKeys to empty arrary when response contains spotlightInfo.
      pinnedItemKeys = [];

      if (spotlightInfo.Items && spotlightInfo.Items.Row) {
        const pinnedItemsMap: { [id: number]: string } = {}; // itemId to itemKey map
        spotlightInfo.Items.Row.forEach((itemFromServer: ISPListRow) => {
          const isFolder = itemFromServer.FSObjType === '1';
          const pinnedItemKey: typeof spItemKeyFacet.type = {
            webAbsoluteUrl: preferredItemSetKey.webAbsoluteUrl,
            listFullUrl: preferredItemSetKey.listFullUrl,
            itemId: itemFromServer.ID,
            rootFolder: isFolder ? itemFromServer.FileRef : ''
          };

          const serializedPinnedItemKey = spItemKeyFacet.serialize(pinnedItemKey);

          const result = processCoreItemResult({
            currentUserId: serverData.CurrentUserId,
            itemFromServer: itemFromServer,
            serializedItemSetKey: serializedItemSetKey,
            serializedParentItemKey: serializedParentItemKey,
            preferredItemSetKey: preferredItemSetKey,
            itemUrlHelper: itemUrlHelper,
            defaultFileIconType: defaultFileIconType,
            isCustomList: isCustomList,
            fieldInfoMap: fieldInfoMap,
            groupBy: groupBy,
            childKeys: [],
            displayFormUrl: serverData.displayFormUrl,
            contentTypes: newListContentTypes || existingListContentTypes,
            isFeatureEnabled: isFeatureEnabled,
            isPromotedLinks: isPromotedLinks,
            isSpotlightItem: true
          });

          itemResolver.merge(result.itemResolver);

          pinnedItemsMap[Number(itemFromServer.ID)] = serializedPinnedItemKey;
        });

        spotlightInfo.Order.forEach((id: number) => {
          const pinnedItemKey = pinnedItemsMap[id];
          if (pinnedItemKey && pinnedItemKeys) {
            pinnedItemKeys.push(pinnedItemKey);

            itemResolver.resolveItems({
              items: {
                [pinnedItemKey]: {
                  ...getInvalidationFacet()
                    .forFacet(deletedFacet).thenInvalidate(pinnedItemSetKey, childItemsFacet)
                    .pack()
                }
              }
            });
          }
        });
      }
      itemResolver.resolveItems({
        items: {
          [pinnedItemSetKey]: {
            ...getInvalidationFacet()
              .forFacet(childItemsFacet).thenInvalidate(serializedItemSetKey, childItemsFacet)
              .pack()
          }
        }
      });
    }

    if (isODB && sharedWidthColumnIndex >= 0) {
      // reorder column for ODB to put sharing at the end.
      columnKeys.push(columnKeys.splice(sharedWidthColumnIndex, 1)[0]);
    }
    return {
      itemResolver,
      columnKeys: columnKeys,
      childKeys: childKeys,
      filterOptionSets: filterOptionSets,
      parentFolderPermissons: listData && listData.FolderPermissions ? fromHexString(listData.FolderPermissions) : undefined,
      progId: listData && listData.CurrentFolderProgId,
      groupChildKeys: groupChildKeys,
      groupLevel,
      hasTitle: serverData.ListSchema && serverData.ListSchema.HasTitle,
      isDocSet: isDocSet,
      pageContext: pageContext,
      itemSetGetAsItemKey: itemSetGetAsItemKey,
      contentTypeKeys: contentTypeKeys,
      folderContentTypeKeys: folderContentTypeKeys,
      pinnedItemKeys: pinnedItemKeys,
      pinnedItemSetKey: pinnedItemSet,
      defaultViewKey: defaultViewKey,
      canModifySchema: canModifySchema,
      hasFieldCustomizer: hasFieldCustomizer,
      spFolderAggregateData: spFolderAggregateData,
      isRootFolder: isRootFolder
    };
  };
}

function isGroupableColumn(fieldName: string, fieldType: string | undefined): boolean {
  if (fieldType === 'URL' || fieldType === 'Note' || fieldType === 'Attachments') {
    return false;
  }
  switch (fieldName) {
    case 'FileSizeDisplay':
    case 'FSObjType':
    case 'DocIcon':
    case 'LinkFilename':
    case 'LinkFilenameNoMenu':
    case 'FileLeafRef':
    case 'LinkTitle':
    case 'LinkTitleNoMenu':
    case 'Title':
      return false;
    default:
      return true;
  }
}

/** For certain group by field, the field value is complex value.
 * Use display name as filter by value
 * Refer to how field value is set in
 * odsp-datasources/src/utilities/list/ItemGroupHelper.ts  _getFilterValue()
 */
function getGroupFilterByValue(group: ISPListGroup): string | undefined {
  let filterValue = group.fieldValue;
  if (group && group.fieldSchema && group.fieldSchema.Type) {
    if (group.fieldSchema.Type === 'User' || group.fieldSchema.Type === 'Lookup') {
      filterValue = group.name;
    } else if (group.fieldSchema.Type === 'DateTime' && group.groupString) {
      let groupStringParts = decodeURIComponent(group.groupString).split(';#');
      let dateTimeString = groupStringParts[0];
      if (groupStringParts.length > 0) { // nested groups
        groupStringParts = groupStringParts.slice(1, groupStringParts.length - 1); // ignore empty first and last items
        if (group.level !== undefined && groupStringParts[group.level]) {
          dateTimeString = groupStringParts[group.level]; // pick the string for the right level
        }
      }

      // convert format yyyymmdd to yyyy-mm-dd
      if (dateTimeString) {
        filterValue = dateTimeString.substring(0, 4) + '-' + dateTimeString.substring(4, 6) + '-' + dateTimeString.substring(6, 8);
      }
    }
  }

  return filterValue;
}

function getUserRating(itemFromServer: ISPListRow, currentUserId: number | undefined): number {
  let ratingString = itemFromServer.Ratings;
  if (ratingString && ratingString.slice(-1) === ',') {
    ratingString = ratingString.slice(0, -1);
  }
  let allRatings = ratingString ? ratingString.split(',').map(Number) : [];
  let ratedByUsers = itemFromServer.RatedBy;
  let userRating = -1;
  let hasUserRated = !!ratedByUsers && ratedByUsers.some((ratedBy: { id: string }, index: number) => {
    if (currentUserId && allRatings && Number(ratedBy.id) === currentUserId) {
      userRating = allRatings[index];
      return true;
    }
    return false;
  });
  return hasUserRated ? userRating : 0;
}

function useDocSetsModernView(isFeatureEnabled: (feature: IFeature) => boolean): boolean {
  return isFeatureEnabled(DocumentSetsModernView);
}

function getFileIconInfo(itemFromServer: ISPListRow, isFeatureEnabled: (feature: IFeature) => boolean, defaultType?: FileIconType): {
  isOneNote: boolean,
  isDocSet: boolean,
  fileIconType: FileIconType,
  isFolder: boolean
} {
  let fileIconType = defaultType !== undefined ? defaultType : 3 as FileIconType.genericFile;
  let isOneNote = false;
  let isDocSet = false;
  let isFolder = false;

  if (itemFromServer.FSObjType === '1') {
    let mapApp = itemFromServer['File_x0020_Type.mapapp'];
    let mapAll = itemFromServer['HTML_x0020_File_x0020_Type.File_x0020_Type.mapall'];

    if ((mapApp && mapApp.substring(0, 7) === 'onenote') ||
      (mapAll && mapAll.indexOf('onenote') !== -1)) {
      isOneNote = true;
    } else if (itemFromServer.ProgId === 'Sharepoint.DocumentSet') {
      fileIconType = 1 as FileIconType.docset;
      if (useDocSetsModernView(isFeatureEnabled)) {
        // Treat DocSets like folders unless their welcome pages are customized
        isDocSet = true;
        if (!itemFromServer.WelcomePageCustomized) {
          isFolder = true;
        }
      }
    } else {
      isFolder = true;
      fileIconType = Number(itemFromServer.PrincipalCount) > 0 ? 5 as FileIconType.sharedFolder : 2 as FileIconType.folder;
    }
  }

  return {
    isOneNote: isOneNote,
    isDocSet: isDocSet,
    fileIconType: fileIconType,
    isFolder: isFolder
  };
}

function getGroup(groupSchema: IGroupSchemaMap, itemFromServer: ISPListRow, groupField: string, level: number,
  isCollapsed: boolean, itemSetKey: typeof spFolderItemSetKeyFacet.type,
  viewKey: typeof spViewKeyFacet.type | undefined, lastGroup0: IGroup, lastGroup1: IGroup,
  columnRealFieldNameMap: { [index: string]: string; }, isFeatureEnabled: (feature: IFeature) => boolean,
  levelOverride?: number,
  serverListSchema?: ISPListSchema): IGroup {
  const parent = level === 1 && lastGroup0 ? lastGroup0.group : undefined;
  const parentGroupKey = level === 1 ? spItemGroupKeyFacet.serialize(lastGroup0.groupKey) : undefined;
  const newGroup = GroupBuilder.buildGroup({ groupSchema: groupSchema }, itemFromServer, groupField, levelOverride ? levelOverride : level,
    isCollapsed /*isCollapsed*/, 0, parent);

  const groupFilterByValue = getGroupFilterByValue(newGroup);

  const spItemGroupKey: typeof spItemGroupKeyFacet.type = {
    groupId: newGroup.groupingId,
    itemSetKey: itemSetKey,
    viewKey: viewKey
  };

  let groupFieldName: string = groupSchema[groupField].DisplayName || groupSchema[groupField].Name;
  if (!newGroup.name) {
    newGroup.name = StringHelper.format(strings.nullValueGroupHeader, groupFieldName);
  } else {
    newGroup.name = StringHelper.format(strings.groupHeader, groupFieldName, newGroup.name);
  }

  if (serverListSchema !== undefined && serverListSchema.hasOwnProperty('Aggregate') && serverListSchema.Aggregate !== undefined) {
    newGroup.columnTotals = getAggregateData(itemFromServer,
      (serverListSchema as ISPListSchema).Aggregate as { [index: string]: string; },
      columnRealFieldNameMap, isFeatureEnabled, levelOverride || level);
  }

  return {
    group: newGroup,
    groupKey: spItemGroupKey,
    parentGroupKey: parentGroupKey,
    groupFieldName: groupField,
    groupFieldType: groupSchema[groupField].Type as FieldType,
    childItemKeys: [],
    childGroupKeys: [],
    groupFilterByValue: groupFilterByValue
  };
}

function getAggregateData(itemFromServer: ISPListRow,
  listSchema: { [index: string]: string; }, mapFieldNameToColumnKey: { [index: string]: string; },
  isFeatureEnabled: (feature: IFeature) => boolean, level?: number): { [index: string]: IAggregateData; } | undefined {
  if (!isFeatureEnabled(ShowColumnTotals)) {
    return undefined;
  }
  let aggregateDataJson: { [index: string]: IAggregateData; } = {};
  let groupAggregate: string;
  groupAggregate = level === undefined ? '' : (level > 0 ? '.agg' + 2 : '.agg');
  const columnNames = Object.getOwnPropertyNames(listSchema);
  for (let i = 0; i < columnNames.length; i++) {
    let aggregateTypeDisplayName: string =
      getAggregateDisplayValue(listSchema[columnNames[i]]);
    let pair: IAggregateData = {
      type: aggregateTypeDisplayName,
      value: itemFromServer[columnNames[i] + '.' + listSchema[columnNames[i]] + groupAggregate],
      aggregateFunctionType: listSchema[columnNames[i]]
    };
    let columnKey: string = mapFieldNameToColumnKey[columnNames[i]];
    if (columnKey) {
      aggregateDataJson[columnKey] = pair;
    }
  }
  return aggregateDataJson;
}

function getSpItemIdentifier(itemFromServer: ISPListRow): string | undefined {
  return itemFromServer.id || itemFromServer.FileRef || itemFromServer.ServerRelativeUrl;
}

function getAllRatings(ratingString: string): number[] {
  if (ratingString && ratingString.slice(-1) === ',') {
    ratingString = ratingString.slice(0, -1);
  }
  let allRatings = ratingString ? ratingString.split(',').map(Number) : [];
  return allRatings;
}
function getRatingsCount(ratedByArray: number[]): number | undefined {
  if (ratedByArray) {
    return ratedByArray.length;
  } else {
    return undefined;
  }
}

function getUserRatedBool(itemFromServer: ISPListRow, currentUserId: number | undefined): boolean {
  let ratingString = itemFromServer.Ratings;
  if (ratingString && ratingString.slice(-1) === ',') {
    ratingString = ratingString.slice(0, -1);
  }
  let allRatings = ratingString ? ratingString.split(',').map(Number) : [];
  let ratedByUsers = itemFromServer.RatedBy;

  let hasUserRated = !!ratedByUsers && ratedByUsers.some((ratedBy: { id: string }, index: number) => {
    if (currentUserId && allRatings && Number(ratedBy.id) === currentUserId) {
      return true;
    }
    return false;
  });
  return hasUserRated;
}

function getUserLikedBool(itemFromServer: ISPListRow, currentUserId: number | undefined): boolean {
  const likedByList = itemFromServer.LikedBy;
  const isLiked = !!likedByList && likedByList.some(
    (likedBy: { id: string }) => Number(likedBy.id) === currentUserId);
  return isLiked;
}

function getCheckedOutStatus(payload: {
  itemFromServer: ISPListRow;
  currentUserId: number | undefined;
}): {
    byYou?: boolean;
    byOther?: string;
    itemResolver: ItemResolver;
  } {
  const {
    itemFromServer,
    currentUserId
  } = payload;

  let checkedOutByYou = false;
  let checkedOutByOtherKey: string | undefined = undefined;

  let checkoutUserId = itemFromServer.CheckedOutUserId;

  let checkedOutOtherId: string | undefined;

  const itemResolver = new ItemResolver();

  if (checkoutUserId) {
    if (currentUserId) {
      checkedOutByYou = isCheckedOutByUser({
        checkedOutUserId: checkoutUserId,
        userId: currentUserId
      });

      if (isCheckedOutByOther({
        checkedOutUserId: checkoutUserId,
        userId: currentUserId
      })) {
        checkedOutOtherId = checkoutUserId;

      }
    } else if (isCheckedOut({ checkedOutUserId: checkoutUserId })) {
      checkedOutOtherId = checkoutUserId;
    }

    if (checkedOutOtherId) {
      let checkoutUserInfo = (itemFromServer.CheckoutUser && itemFromServer.CheckoutUser[0]) || null;
      if (checkoutUserInfo) {
        const userKey = spPersonKeyFacet.serialize({
          id: String(checkoutUserInfo.id)
        });

        checkedOutByOtherKey = userKey;
        itemResolver.resolveItems({
          items: {
            [userKey]: pack(userFacets, {
              key: {
                id: String(checkoutUserInfo.id)
              },
              displayName: checkoutUserInfo.title,
              email: checkoutUserInfo.email,
              picture: checkoutUserInfo.picture,
              sip: checkoutUserInfo.sip
            })
          }
        });
      }
    }
  }

  return {
    byYou: checkedOutByYou,
    byOther: checkedOutByOtherKey,
    itemResolver
  };
}

interface IMissingMetadataInfo {
  hasMissingMetadata: typeof missingMetadataFacet.type;
  fields: typeof missingMetadataFieldsFacet.type;
}

function getMissingMetadataStatus(itemFromServer: ISPListRow, contentTypes?: ISPContentType[],
  isSpotlightItem?: boolean): IMissingMetadataInfo {
  const missingMetadata: IMissingMetadataInfo = {
    hasMissingMetadata: false,
    fields: {}
  };
  if (!isSpotlightItem && contentTypes && contentTypes.length > 0 && itemFromServer.ContentTypeId) {
    for (const contentType of contentTypes) {
      if (itemFromServer.ContentTypeId === contentType.contentTypeId) {
        if (contentType.requiredFields) {
          const requiredFields = contentType.requiredFields.split(',');
          for (const requiredColumnName of requiredFields) {
            const columnData = itemFromServer[requiredColumnName];
            if (!columnData || (Array.isArray(columnData) && columnData.length === 0)) {
              missingMetadata.hasMissingMetadata = true;
              missingMetadata.fields[requiredColumnName] = true;
            }
          }
        }
      }
    }
  }
  return missingMetadata;
}

function getMalwareDetected(itemFromServer: ISPListRow): boolean {
  const fileVirusStatus = Number(itemFromServer._VirusStatus) || 0;
  if ((fileVirusStatus !== 0) &&
    (fileVirusStatus !== 3)) {
    return true;
  }
  return false;
}

interface IProcessCoreItemResultParams {
  currentUserId: number | undefined;
  itemFromServer: ISPListRow;
  serializedParentItemKey: Key<typeof spItemKeyFacet.type>;
  serializedItemSetKey: Key<typeof spFolderItemSetKeyFacet.type>;
  preferredItemSetKey: typeof spFolderItemSetKeyFacet.type;
  itemUrlHelper: ItemUrlHelperBasic;
  defaultFileIconType: FileIconType | undefined;
  isCustomList: boolean;
  isPromotedLinks: boolean;
  fieldInfoMap: {
    [key: string]: FieldType
  };
  groupBy: string[];
  childKeys: string[];
  displayFormUrl: string | undefined;
  contentTypes: ISPContentType[] | undefined;
  isFeatureEnabled?: (feature: IFeature) => boolean;
  listViewAdapter?: IListViewAdapter;
  userFieldLookupFields?: { [fieldName: string]: string };
  isSpotlightItem?: boolean;
  isLinksList?: boolean;
}

function processCoreItemResult(params: IProcessCoreItemResultParams): {
  isItemResolved: boolean;
  itemResolver: ItemResolver;
  childKey: string;
} {
  const {
    itemFromServer,
    serializedParentItemKey,
    serializedItemSetKey,
    currentUserId,
    preferredItemSetKey,
    itemUrlHelper,
    defaultFileIconType,
    isCustomList,
    fieldInfoMap,
    groupBy,
    childKeys,
    displayFormUrl,
    contentTypes,
    isFeatureEnabled = (feature: IFeature) => false,
    listViewAdapter,
    isPromotedLinks,
    userFieldLookupFields,
    isSpotlightItem,
    isLinksList
  } = params;
  let isItemResolved = false;
  const parentItemKey = preferredItemSetKey;
  const itemUrlParts = itemUrlHelper.getUrlParts({
    path: itemFromServer.id || itemFromServer.FileRef || itemFromServer.ServerRelativeUrl,
    listUrl: parentItemKey.listFullUrl,
    webUrl: parentItemKey.webAbsoluteUrl
  });

  const itemResolver = new ItemResolver();

  const fileIconInfo = getFileIconInfo(itemFromServer, isFeatureEnabled, defaultFileIconType);

  // getFileIconInfo has speical logic to handle onenote and docset etc, to ensure they are not treated as folder.
  const isFolder = fileIconInfo.isFolder;
  const isDocSet = fileIconInfo.isDocSet;

  const childItem: typeof spItemKeyFacet.type = {
    webAbsoluteUrl: parentItemKey.webAbsoluteUrl,
    listFullUrl: parentItemKey.listFullUrl,
    itemId: itemFromServer.ID
  };
  const childKey = spItemKeyFacet.serialize(childItem);

  const childItemWithPathAndId = isFolder ? {
    webAbsoluteUrl: parentItemKey.webAbsoluteUrl,
    listFullUrl: parentItemKey.listFullUrl,
    itemId: itemFromServer.ID,
    rootFolder: itemFromServer.FileRef
  } : undefined;
  const pathOnlyChildItem = childItemWithPathAndId ? getNormalizedFolderKey(childItemWithPathAndId) : undefined;
  const pathOnlyChildItemKey = pathOnlyChildItem && spItemKeyFacet.serialize(pathOnlyChildItem);

  if (childItemWithPathAndId) {
    itemResolver.mapKeys({
      mappings: {
        [spItemKeyFacet.serialize(childItemWithPathAndId)]: childKey
      }
    });
  }

  if (pathOnlyChildItemKey) {
    itemResolver.mapKeys({
      mappings: {
        [pathOnlyChildItemKey]: childKey
      }
    });
  }

  const {
    webAbsoluteUrl,
    listFullUrl
  } = spItemKeyFacet.deserialize(serializedParentItemKey);

  const requestMatchingChildItemKey = spItemKeyFacet.serialize({
    ...childItem,
    webAbsoluteUrl,
    listFullUrl
  });

  // Since the original request for this item may not have been well-formed,
  // map the request key to the new, proper key.
  itemResolver.mapKeys({
    mappings: {
      [requestMatchingChildItemKey]: childKey
    }
  });

  if (groupBy.length === 0) {
    childKeys.push(childKey);
  }

  let invalidationFacet = getInvalidationFacet()
    .forFacet(titleFacet).thenInvalidate(serializedItemSetKey, childItemsFacet)
    .forFacet(displayNameFacet).thenInvalidate(serializedItemSetKey, childItemsFacet)
    .forFacet(deletedFacet).thenInvalidate(serializedItemSetKey, childItemsFacet)
    .forFacet(itemMediaTransformFacet).thenInvalidate(serializedItemSetKey, childItemsFacet)
    .forFacet(defaultChildItemSetFacet).thenInvalidate(childKey, countFacet);

  const isNeedsAttentionView = preferredItemSetKey && preferredItemSetKey.viewKey &&
    preferredItemSetKey.viewKey.viewId === needsAttentionViewId;
  if (!isNeedsAttentionView) {
    for (const propName in itemFromServer) {
      if (Object.prototype.hasOwnProperty.call(itemFromServer, propName)) {
        let fieldType = fieldInfoMap[propName];
        if (fieldType) {
          const facet = getFieldFacet(propName, fieldType);
          const propValue = itemFromServer[propName];

          invalidationFacet = invalidationFacet
            .forFacet(facet).thenInvalidate(serializedItemSetKey, childItemsFacet);

          switch (fieldType) {
            case 'User':
            case 'UserMulti':
              if (propName !== '_ComplianceTagUserId' && propValue && propValue.length > 0) {
                propValue.forEach((user: IEditor) => {
                  const lookupField = userFieldLookupFields && userFieldLookupFields[propName] || '';
                  const userKey = spPersonKeyFacet.serialize({
                    id: String(user.id)
                  });
                  itemResolver.resolveItems({
                    items: {
                      [userKey]: pack(userFacets, {
                        key: {
                          id: String(user.id)
                        },
                        ...(lookupField ? {} : { displayName: user.title }),
                        email: user.email,
                        picture: user.picture,
                        sip: user.sip
                      })
                    }
                  });
                });
              }
              break;
          }
        }
      }
    }
  }

  let modifiedUserKey = '';
  let modifiedUserDisplayValue = '';
  if (itemFromServer.Editor) {
    const editor = itemFromServer.Editor[0];
    const lookupField = userFieldLookupFields && userFieldLookupFields.Editor || '';

    modifiedUserKey = spPersonKeyFacet.serialize({
      id: String(editor.id),
    });

    modifiedUserDisplayValue = lookupField ? editor.title : '';

    itemResolver.resolveItems({
      items: {
        [modifiedUserKey]: pack(userFacets, {
          key: {
            id: String(editor.id)
          },
          ...(lookupField ? {} : { displayName: editor.title }),
          email: editor.email,
          picture: editor.picture,
          sip: editor.sip
        })
      }
    });
  }

  let openUrl = itemFromServer['serverurl.progid'];
  if (isCustomList && !isFolder && displayFormUrl) {
    // This logic might need to test more when we support shortCutLink
    openUrl = getListItemFormUrl(
      displayFormUrl,
      preferredItemSetKey.rootFolder,
      itemFromServer.ID, itemFromServer.ContentTypeId);
  } else if (!!openUrl) {
    openUrl = openUrl.substring(1);
    if (openUrl && openUrl.toLowerCase().indexOf('/wopiframe?') !== -1) {
      openUrl = openUrl.replace(/\/_layouts\/WopiFrame.aspx\?/, '/_layouts/15/WopiFrame.aspx?');
    }
  } else if (itemUrlParts.fullItemUrl) {
    openUrl = Path.canonicalizeDecodedUrl(itemUrlParts.fullItemUrl);
  }

  let itemDisplayName = isCustomList || isPromotedLinks ? itemFromServer.Title : itemFromServer.FileLeafRef;
  if (isLinksList) {
    itemDisplayName = itemFromServer['URL.desc'] || itemFromServer.URL || itemDisplayName;
  }

  const welcomeViewId = isFolder && useDocSetsModernView(isFeatureEnabled) ?
    Guid.normalizeLower(itemFromServer.WelcomeViewId, false/*false removes brackets*/) :
    undefined;

  if (itemFromServer.ID && itemFromServer.FileRef) {
    const permissions = itemFromServer.PermMask ? fromHexString(itemFromServer.PermMask) : undefined;
    const appMap = itemFromServer['File_x0020_Type.mapapp'] || itemFromServer['HTML_x0020_File_x0020_Type.File_x0020_Type.mapall'];

    const hasThumbnail = itemFromServer['.hasThumbnail'] === 'True';
    const missingMetadataInfo = getMissingMetadataStatus(itemFromServer, contentTypes, isSpotlightItem);

    const spItemUrl = itemFromServer['.spItemUrl'];
    const eTag = itemFromServer['.etag'];
    const driveItemKey = spItemUrl ? getDriveItemKey(spItemUrl) : undefined;

    if (driveItemKey) {
      const driveItemKeys = getEndpointVersionKeyAliases(driveItemKey);

      for (const itemKey of driveItemKeys) {
        itemResolver.mapKeys({
          mappings: {
            [itemKey]: childKey
          }
        });
      }
    }

    isItemResolved = true;

    const {
      fullItemUrl
    } = itemUrlParts;

    const rowAdapter = listViewAdapter &&
      listViewAdapter.createRowAdapter({ values: itemFromServer });

    const hasFriendlyDateModified = 'Modified.FriendlyDisplay' in itemFromServer;
    const friendlyDateModified = itemFromServer['Modified.FriendlyDisplay'];
    const friendlyDateModifiedText = friendlyDateModified ? getRelativeDateTimeStringForLists(friendlyDateModified) : '';
    const friendlyDateModifiedNotNull = friendlyDateModifiedText === null ? undefined : friendlyDateModifiedText;
    const hasRawDateModified = 'Modified.' in itemFromServer;
    const rawDateModified = itemFromServer['Modified.'];
    const moderationStatusTypeString = itemFromServer['_ModerationStatus.'];
    const moderationStatusType = !!moderationStatusTypeString ?
      parseInt(moderationStatusTypeString, 10) as ModerationStatusType : undefined;
    const moderationComments = itemFromServer._ModerationComments;

    const defaultChildItemSetKey = pathOnlyChildItem ? spFolderItemSetKeyFacet.serialize({
      webAbsoluteUrl: pathOnlyChildItem.webAbsoluteUrl,
      listFullUrl: pathOnlyChildItem.listFullUrl,
      rootFolder: pathOnlyChildItem.rootFolder || ''
    }) : undefined;

    const {
      itemResolver: checkedOutItemResolved,
      ...checkedOutStatus
    } = getCheckedOutStatus({
      itemFromServer,
      currentUserId
    });

    const parentFolderItem = getParentItemKey({
      webAbsoluteUrl,
      listFullUrl,
      rootFolder: itemFromServer.FileRef
    });

    const parentItemRootFolder = `${new SimpleUri(webAbsoluteUrl).authority}${itemFromServer.FileRef}`.split('/').slice(0, -1).join('/');
    const parentFolderItemKey = parentFolderItem && spItemKeyFacet.serialize(parentFolderItem);

    itemFromServer['.missingMetadataFacet'] = missingMetadataInfo.hasMissingMetadata;
    itemFromServer['.missingMetadataFieldsFacet'] = missingMetadataInfo.fields;
    itemResolver.resolveItems({
      items: {
        [childKey]: {
          ...spItemKeyFacet.pack(pathOnlyChildItem ? pathOnlyChildItem : childItem),
          ...preferredKeyFacetFacet.pack(spItemKeyFacet),
          ...(driveItemKey ? {
            ...driveItemKeyFacet.pack(driveItemKey),
            ...analyticsItemFacet.pack(
              { itemKey: driveAnalyticsItemKeyFacet.serialize({ driveItemKey: driveItemKey }) }
            ),
            ...followedItemFacet.pack(
              { itemKey: driveFollowedItemKeyFacet.serialize({ driveItemKey: driveItemKey }) }
            ),
            ...activityItemFacet.pack(
              { itemKey: driveActivityItemKeyFacet.serialize({ driveItemKey: driveItemKey }) }
            )
          } : {}),
          ...likedByItemFacet.pack({
            likedByItemKey: likedByItemKeyFacet.serialize({ spItem: childItem })
          }),
          ...displayNameFacet.pack(itemDisplayName),
          ...(!isSpotlightItem ? titleFacet.pack(itemFromServer.Title) : {}),
          ...fileNameFacet.pack(getFileName(itemFromServer)),
          ...parentFolderItemFacet.pack(parentFolderItemKey ? {
            itemKey: parentFolderItemKey
          } : undefined),
          ...deletedFacet.pack(undefined),
          ...folderPathFacet.pack(itemFromServer.FileRef),
          ...(moderationStatusType !== undefined ? moderationStatusTypeFacet.pack(moderationStatusType) : {}),
          ...(moderationComments !== undefined ? moderationCommentsFacet.pack(moderationComments) : {}),
          ...(hasRawDateModified && rawDateModified ? dateModifiedFacet.pack(new Date(rawDateModified).getTime()) : {}),
          ...(hasFriendlyDateModified ? friendlyDateModifiedFacet.pack(friendlyDateModifiedNotNull) : {}),
          ...('PrincipalCount' in itemFromServer ?
            isSharedFacet.pack(!!itemFromServer.PrincipalCount && itemFromServer.PrincipalCount !== '0') :
            {}),
          ...averageRatingFacet.pack(itemFromServer.AverageRating ? parseFloat(itemFromServer.AverageRating) : undefined),
          ...ratingCountFacet.pack(getRatingsCount(itemFromServer.RatedBy)),
          ...hasUserRatedFacet.pack(getUserRatedBool(itemFromServer, currentUserId)),
          ...allRatingsFacet.pack(getAllRatings(itemFromServer.Ratings)),
          ...likesCountFacet.pack(Number(itemFromServer.LikesCount)),
          ...hasUserLikedFacet.pack(getUserLikedBool(itemFromServer, currentUserId)),
          ...(appMap ? officeAppFacet.pack(appMap) : {}),
          ...fileSizeFacet.pack(itemFromServer.File_x0020_Size ?
            parseInt(itemFromServer.File_x0020_Size, 10) :
            itemFromServer.FileSizeDisplay ?
              parseInt(itemFromServer.FileSizeDisplay, 10) :
              undefined),
          ...(modifiedUserKey ? modifiedByPersonFacet.pack({
            itemKey: modifiedUserKey,
            displayValue: modifiedUserDisplayValue
          }) : {}),
          ...(permissions ? permissionsFacet.pack(permissions) : {}),
          ...(!hasThumbnail ? thumbnailFacet.pack(undefined) : {}),
          ...folderFacet.pack(isFolder ? {} : undefined),
          ...countFacet.pack(isFolder ? getChildCount(itemFromServer) : undefined),
          ...oneNoteFacet.pack(fileIconInfo.isOneNote ? {} : undefined),
          ...documentSetFacet.pack(isDocSet ? {} : undefined),
          ...fileIconTypeFacet.pack(fileIconInfo.fileIconType),
          ...getMediaFacets(itemFromServer),
          ...(itemFromServer.UniqueId ? uniqueIdFacet.pack(normalizeLower(itemFromServer.UniqueId)) : {}),
          ...(eTag ? eTagFacet.pack(eTag) : {}),
          ...listItemIdFacet.pack(itemFromServer.ID),
          ...openUrlFacet.pack(openUrl),
          ...(welcomeViewId ? preferredViewFacet.pack({ viewKey: { viewId: welcomeViewId } }) : {}),
          ...(fullItemUrl ? canonicalUrlFacet.pack(fullItemUrl) : {}),
          ...userRatingFacet.pack(getUserRating(itemFromServer, currentUserId)),
          ...spItemIdentifierFacet.pack(getSpItemIdentifier(itemFromServer)),
          ...buildItemMediaTransformFacets(itemFromServer),
          ...defaultChildItemSetFacet.pack(defaultChildItemSetKey ? {
            itemKey: defaultChildItemSetKey
          } : undefined),
          ...invalidationFacet.pack(),
          ...isNewFacet.pack((Number(itemFromServer['Created_x0020_Date.ifnew']) === 1)),
          ...isCheckedOutFacet.pack(checkedOutStatus),
          ...missingMetadataFacet.pack(missingMetadataInfo.hasMissingMetadata),
          ...pack({
            missingMetadata: missingMetadataFieldsFacet
          }, {
              missingMetadata: new MirrorPatch(missingMetadataInfo.fields)
            }),
          ...malwareDetectedFacet.pack(getMalwareDetected(itemFromServer)),
          ...policyTipFacet.pack(itemFromServer._ip_UnifiedCompliancePolicyUIAction ?
            Number(itemFromServer._ip_UnifiedCompliancePolicyUIAction) : PolicyTipType.none),
          ...retentionPolicyFacet.pack(getComplianceRecordValue(itemFromServer, isFeatureEnabled(IPRecordVersioningClient)) ?
            RetentionPolicyType.record : RetentionPolicyType.none),
          ...showRetentionPolicyIconFacet.pack(getComplianceRecordIconValue(itemFromServer, isFeatureEnabled(IPRecordVersioningClient))),
          ...rowDataFacet.pack(itemFromServer),
          ...(spItemUrl ? vroomUrlFacet.pack(spItemUrl) : {}),
          ...contentTypeIdFacet.pack(itemFromServer.ContentTypeId),
          ...(rowAdapter ? rowAdapterFacet.pack(rowAdapter) : {}),
          ...(isPromotedLinks ? {
            ...launchBehaviorFacet.pack(itemFromServer.LaunchBehavior),
            ...linkLocationFacet.pack(itemFromServer.LinkLocation)
          } : {})
        },
        ...(defaultChildItemSetKey && pathOnlyChildItemKey ? {
          // Ensure that any discovered folder is registered as the parent of its default set.
          [defaultChildItemSetKey]: {
            ...parentItemFacet.pack({
              itemKey: pathOnlyChildItemKey
            })
          }
        } : {}),
        ...(parentFolderItemKey ? {
          [parentFolderItemKey]: {
            ...folderFacet.pack({}),
            ...canonicalUrlFacet.pack(parentItemRootFolder),
            ...openUrlFacet.pack(parentItemRootFolder)
          }
        } : {})
      }
    });
  }

  return {
    isItemResolved: isItemResolved,
    childKey: childKey,
    itemResolver
  };
}

function getChildCount(itemFromServer: ISPListRow): typeof countFacet.type | undefined {
  const itemChildCount = itemFromServer.ItemChildCount ? parseInt(itemFromServer.ItemChildCount, 10) : NaN;
  const folderChildCount = itemFromServer.FolderChildCount ? parseInt(itemFromServer.FolderChildCount, 10) : NaN;

  if (isNaN(itemChildCount) && isNaN(folderChildCount)) {
    return undefined;
  } else if (isNaN(itemChildCount)) {
    return folderChildCount;
  } else if (isNaN(folderChildCount)) {
    return itemChildCount;
  } else {
    return itemChildCount + folderChildCount;
  }
}

/**
 * Extracts the file name parts
 * Preserves the '.' in the extension.
 */
function getFileName(itemFromServer: ISPListRow): typeof fileNameFacet.type {
  const extension = itemFromServer.File_x0020_Type ? '.' + itemFromServer.File_x0020_Type : '';
  const fullFileName = itemFromServer.FileLeafRef ||
    itemFromServer.FileRef && new SimpleUri(itemFromServer.FileRef).segments.slice(-1)[0] ||
    '';
  const lastIndexOfExtension = extension && fullFileName ?
    fullFileName.toLowerCase().lastIndexOf(extension.toLowerCase()) : -1;

  return {
    name: lastIndexOfExtension > -1 ? fullFileName.substring(0, lastIndexOfExtension) : fullFileName,
    extension: extension
  };
}

/**
 * Get the mapped FieldType from the ISPListField
 */
function getFieldType(field: ISPListField): FieldType {
  let fieldType = field.Type as FieldType;
  if (field.FieldType &&
    (field.FieldType === 'TaxonomyFieldType' || field.FieldType === 'TaxonomyFieldTypeMulti')) {
    // for Taxonomy field, field.Type is 'Lookup', use field.FieldType instead
    fieldType = field.FieldType;
  } else if (field.RenderAsText) {
    fieldType = 'Text';
  }

  if (field.AppendOnly === 'TRUE') {
    fieldType = 'TextHtml';
  }

  if (field.FieldType && field.FieldType === 'Note' && field.RichText === 'TRUE') {
    fieldType = 'NoteRichText';
  }

  if (field.FieldType === 'URL') {
    if (field.Format === 'Hyperlink') {
      fieldType = 'Hyperlink';
    } else {
      fieldType = 'Image';
    }
  }

  if (field.Name === '_ComplianceFlags' || field.Name === '_ComplianceTag') {
    fieldType = 'Text';
  } else if (field.Name === '_ComplianceTagUserId') {
    fieldType = 'User';
  } else if (field.Name === '_ComplianceTagWrittenTime') {
    fieldType = 'DateTime';
  } else if (field.Name === 'URLwMenu' || field.Name === 'URLNoMenu') {
    fieldType = 'Hyperlink';
  }

  if (field.Name === 'Thumbnail') {
    fieldType = 'Thumbnail';
  }

  return fieldType;
}

function getMediaFacets(itemFromServer: ISPListRow): IFaceted {
  const mediaServiceFastMetadata = getMediaServiceFastMetadata(itemFromServer);

  const photoFromServer = mediaServiceFastMetadata && mediaServiceFastMetadata.photo;
  const videoFromServer = mediaServiceFastMetadata && mediaServiceFastMetadata.video;

  const dimensionable =
    videoFromServer && {
      width: videoFromServer.frameWidth,
      height: videoFromServer.frameHeight
    } ||
    photoFromServer && {
      width: photoFromServer.width,
      height: photoFromServer.height
    };

  const dimensions = dimensionable && typeof dimensionable.width === 'number' && typeof dimensionable.height === 'number' ? {
    width: dimensionable.width,
    height: dimensionable.height
  } : undefined;

  return {
    ...(photoFromServer ? photoFacet.pack({}) : {}),
    ...(videoFromServer ? videoFacet.pack({}) : {}),
    ...dimensionsFacet.pack(dimensions),
  };
}

function getMediaServiceFastMetadata(itemFromServer: ISPListRow): IMediaServiceMetadata | undefined {
  let mediaServiceFastMetadata: IMediaServiceMetadata | undefined;

  try {
    if (itemFromServer.MediaServiceFastMetadata) {
      mediaServiceFastMetadata = JSON.parse(itemFromServer.MediaServiceFastMetadata) as IMediaServiceMetadata;
    }
  } catch (e) {
    // Ignore parsing errors.
  }

  return mediaServiceFastMetadata;
}

function prepareClientSideCustomizedFields(
  fieldCustomizersParam: string
): IFieldCustomizerData | undefined {
  if (!fieldCustomizersParam) {
    return;
  }

  let customizedFields: IFieldCustomizerData = {};
  let rawCustomizerDict: ICustomizedFieldsDict;

  try {
    rawCustomizerDict = JSON.parse(fieldCustomizersParam);
    if (rawCustomizerDict) {
      for (const fieldKey of Object.keys(rawCustomizerDict)) {
        let fieldCustomizerInfo = rawCustomizerDict[fieldKey];
        let componentId = '';
        let componentProperties = '';
        // It can be Guid for the componentId without properties
        // Or can be format of {'id': guidValue, 'properties': propertyValue}
        if (typeof fieldCustomizerInfo === 'string') {
          componentId = fieldCustomizerInfo;
        } else if (typeof fieldCustomizerInfo === 'object') {
          if (fieldCustomizerInfo.id) {
            componentId = fieldCustomizerInfo.id;
          }
          if (fieldCustomizerInfo.properties) {
            let properties = '';
            if (typeof fieldCustomizerInfo.properties === 'object') {
              properties = JSON.stringify(fieldCustomizerInfo.properties);
            }
            componentProperties = properties;
          }
        }

        customizedFields[fieldKey] = {
          clientSideComponentId: componentId,
          clientSideComponentProperties: componentProperties
        };
      }
    }
  } catch (e) {
    console.log('Error when parse debug fieldCustomizer parameter: ' + fieldCustomizersParam + ', error: ' + e);
  }

  return customizedFields;
}